/**
 * getDictionary loader
 * Loads and merges translations in this order:
 *  a) /i18n/{locale}/common.json
 *  b) /i18n/{locale}/{namespace}/common.json
 *  c) /i18n/{locale}/{namespace}/*.json  (all except common & sidebar, except the context file)
 *  d) /i18n/{locale}/{namespace}/{context}.json  (specific override, if provided)
 *
 * Resilient: missing files are silently ignored.
 * Dynamic: new namespaces/contexts are auto-discovered at build time via require.context —
 *          no manual import registration needed.
 */

// Support both webpack's `require.context` and Vite's `import.meta.glob`.
type WebpackContext = ((id: string) => any) & { keys(): string[] };

let i18nKeys: string[] = [];
let i18nLoader: ((path: string) => any | null) | null = null;

if (typeof (globalThis as any).require !== 'undefined' && (globalThis as any).require.context) {
  const req = (globalThis as any).require as any;
  const i18nCtx: WebpackContext = req.context('../../i18n', true, /\.json$/);
  i18nKeys = i18nCtx.keys();
  i18nLoader = (p: string) => {
    try { return i18nCtx(p); } catch { return null; }
  };
} else {
  // Vite: import all JSON files under src/i18n at build time.
  // The glob keys look like '../../i18n/en-US/common.json' relative to this file.
  // @ts-ignore - import.meta.glob typing
  const modules = import.meta.glob('../../i18n/**/*.json', { query: '?json', eager: true }) as Record<string, any>;
  const prefix = '../../i18n/';
  for (const fullPath of Object.keys(modules)) {
    const rel = './' + fullPath.slice(prefix.length);
    i18nKeys.push(rel);
  }
  const map: Record<string, any> = {};
  for (const fullPath of Object.keys(modules)) {
    const rel = './' + fullPath.slice(prefix.length);
    map[rel] = modules[fullPath];
  }
  i18nLoader = (p: string) => (map[p] ?? null);
}

function tryLoad(_ctx: any, path: string): any | null {
  if (!i18nLoader) return null;
  try { return i18nLoader(path); } catch { return null; }
}

function deepMerge(target: any, source: any) {
  for (const key of Object.keys(source || {})) {
    const srcVal = source[key];
    const tgtVal = target[key];
    if (Array.isArray(srcVal)) {
      target[key] = srcVal.slice();
    } else if (srcVal && typeof srcVal === 'object') {
      if (!tgtVal || typeof tgtVal !== 'object') target[key] = {};
      deepMerge(target[key], srcVal);
    } else {
      target[key] = srcVal;
    }
  }
}

export async function getDictionary(locale: string, namespace: string, context?: string) {
  const merged: any = {};

  // a) top-level common
  const common = tryLoad(null, `./${locale}/common.json`);
  if (common) deepMerge(merged, common.default ?? common);

  // b) namespace common
  const nsCommon = tryLoad(null, `./${locale}/${namespace}/common.json`);
  if (nsCommon) deepMerge(merged, nsCommon.default ?? nsCommon);

  // c) all other files in the namespace (auto-discovered at build time)
  //    Allows per-group files like assets/poultry.json or units/farm.json to extend the base.
  //    The specific context file (step d) is intentionally skipped here so it is applied last.
  const nsPrefix = `./${locale}/${namespace}/`;
  for (const key of i18nKeys) {
    if (!key.startsWith(nsPrefix)) continue;
    const fileName = key.slice(nsPrefix.length);
    if (fileName.includes('/')) continue; // ignore sub-subdirectories (handled in step c2)
    if (fileName === 'common.json' || fileName === 'sidebar.json') continue;
    if (context && fileName === `${context}.json`) continue; // reserved for step d
    if (!i18nLoader) continue;
    try {
      const mod = i18nLoader(key);
      deepMerge(merged, mod?.default ?? mod);
    } catch {}
  }

  // c2) for assets namespace, load groups as subdirectories (bovines/, poultry/, etc.)
  //     Each group can have common.json and context-specific files
  if (namespace === 'assets') {
    const groupsMap: Record<string, string[]> = {};
    
    // Discover all groups and their files
    for (const key of i18nKeys) {
      if (!key.startsWith(nsPrefix)) continue;
      const relativePath = key.slice(nsPrefix.length);
      const parts = relativePath.split('/');
      if (parts.length < 2) continue; // skip flat files (already handled in step c)
      
      const groupName = parts[0];
      if (!groupsMap[groupName]) groupsMap[groupName] = [];
      groupsMap[groupName].push(key);
    }
    
    // Load default as base (if exists)
    let defaultData: any = {};
    if (groupsMap['default']) {
      const defaultFiles = groupsMap['default'];
      const defaultCommon = defaultFiles.find(f => f.endsWith('default/common.json'));
      if (defaultCommon && i18nLoader) {
        try {
          const mod = i18nLoader(defaultCommon);
          deepMerge(defaultData, mod?.default ?? mod);
        } catch {}
      }
      // Load other files in default group
      for (const file of defaultFiles) {
        if (file === defaultCommon) continue;
        if (file.endsWith('/common.json')) continue;
        if (!i18nLoader) continue;
        try {
          const mod = i18nLoader(file);
          deepMerge(defaultData, mod?.default ?? mod);
        } catch {}
      }
      // Store default as its own key
      merged['default'] = defaultData;
    }
    
    // Load each group (merge base namespace common, then default, then group-specific data)
    for (const [groupName, files] of Object.entries(groupsMap)) {
      if (groupName === 'default') continue; // already handled above
      
      // Start with a copy of namespace common (assets/common.json) as the ultimate base
      const groupData: any = {};
      deepMerge(groupData, nsCommon?.default ?? nsCommon ?? {});
      // Then merge default group data on top
      deepMerge(groupData, defaultData);
      
      // Load group's common.json and merge on top of default
      const groupCommon = files.find(f => f.endsWith(`${groupName}/common.json`));
      if (groupCommon && i18nLoader) {
        try {
          const mod = i18nLoader(groupCommon);
          deepMerge(groupData, mod?.default ?? mod);
        } catch {}
      }
      
      // Load other files in the group (context overrides)
      for (const file of files) {
        if (file === groupCommon) continue; // already loaded
        if (file.endsWith('/common.json')) continue;
        if (!i18nLoader) continue;
        try {
          const mod = i18nLoader(file);
          deepMerge(groupData, mod?.default ?? mod);
        } catch {}
      }
      
      // Merge group data under group name
      merged[groupName] = groupData;
    }
  }

  // c3) for settings namespace, load nested structure (settings/assets/categories/, etc.)
  //     This supports hierarchical organization of settings translations
  if (namespace === 'settings') {
    const nestedMap: Record<string, any> = {};
    
    // Discover all nested paths and their files
    for (const key of i18nKeys) {
      if (!key.startsWith(nsPrefix)) continue;
      const relativePath = key.slice(nsPrefix.length);
      const parts = relativePath.split('/');
      if (parts.length < 2) continue; // skip flat files (already handled in step c)
      
      // Build nested structure based on directory path
      // e.g., assets/categories/common.json -> merged.assets.categories
      let current = nestedMap;
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!current[part]) current[part] = {};
        current = current[part];
      }
      
      // Load the file and merge it into the nested structure
      if (!i18nLoader) continue;
      try {
        const mod = i18nLoader(key);
        const data = mod?.default ?? mod;
        deepMerge(current, data);
      } catch {}
    }
    
    // Merge nested structure into merged
    deepMerge(merged, nestedMap);
  }

  // d) context-specific override (applied last so it wins over generic namespace files)
  if (context) {
    const ctxMod = tryLoad(null, `./${locale}/${namespace}/${context}.json`);
    if (ctxMod) deepMerge(merged, ctxMod?.default ?? ctxMod);
  }

  return merged;
}

/**
 * Synchronous version of getDictionary.
 * In Vite, all JSON files are eagerly loaded via import.meta.glob so the loader
 * is already synchronous — this avoids the async tick that causes FOUC.
 */
export function getDictionarySync(locale: string, namespace: string, context?: string): any {
  const merged: any = {};

  const common = tryLoad(null, `./${locale}/common.json`);
  if (common) deepMerge(merged, common.default ?? common);

  const nsCommon = tryLoad(null, `./${locale}/${namespace}/common.json`);
  if (nsCommon) deepMerge(merged, nsCommon.default ?? nsCommon);

  const nsPrefix = `./${locale}/${namespace}/`;
  for (const key of i18nKeys) {
    if (!key.startsWith(nsPrefix)) continue;
    const fileName = key.slice(nsPrefix.length);
    if (fileName.includes('/')) continue;
    if (fileName === 'common.json' || fileName === 'sidebar.json') continue;
    if (context && fileName === `${context}.json`) continue;
    if (!i18nLoader) continue;
    try {
      const mod = i18nLoader(key);
      deepMerge(merged, mod?.default ?? mod);
    } catch {}
  }

  if (namespace === 'assets') {
    const groupsMap: Record<string, string[]> = {};
    for (const key of i18nKeys) {
      if (!key.startsWith(nsPrefix)) continue;
      const relativePath = key.slice(nsPrefix.length);
      const parts = relativePath.split('/');
      if (parts.length < 2) continue;
      const groupName = parts[0];
      if (!groupsMap[groupName]) groupsMap[groupName] = [];
      groupsMap[groupName].push(key);
    }
    let defaultData: any = {};
    if (groupsMap['default']) {
      const defaultFiles = groupsMap['default'];
      const defaultCommon = defaultFiles.find(f => f.endsWith('default/common.json'));
      if (defaultCommon && i18nLoader) {
        try { const mod = i18nLoader(defaultCommon); deepMerge(defaultData, mod?.default ?? mod); } catch {}
      }
      for (const file of defaultFiles) {
        if (file === defaultCommon || file.endsWith('/common.json') || !i18nLoader) continue;
        try { const mod = i18nLoader(file); deepMerge(defaultData, mod?.default ?? mod); } catch {}
      }
      merged['default'] = defaultData;
    }
    for (const [groupName, files] of Object.entries(groupsMap)) {
      if (groupName === 'default') continue;
      const groupData: any = {};
      deepMerge(groupData, nsCommon?.default ?? nsCommon ?? {});
      deepMerge(groupData, defaultData);
      const groupCommon = files.find(f => f.endsWith(`${groupName}/common.json`));
      if (groupCommon && i18nLoader) {
        try { const mod = i18nLoader(groupCommon); deepMerge(groupData, mod?.default ?? mod); } catch {}
      }
      for (const file of files) {
        if (file === groupCommon || file.endsWith('/common.json') || !i18nLoader) continue;
        try { const mod = i18nLoader(file); deepMerge(groupData, mod?.default ?? mod); } catch {}
      }
      merged[groupName] = groupData;
    }
  }

  if (namespace === 'settings') {
    const nestedMap: Record<string, any> = {};
    for (const key of i18nKeys) {
      if (!key.startsWith(nsPrefix)) continue;
      const relativePath = key.slice(nsPrefix.length);
      const parts = relativePath.split('/');
      if (parts.length < 2) continue;
      let current = nestedMap;
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!current[part]) current[part] = {};
        current = current[part];
      }
      if (!i18nLoader) continue;
      try {
        const mod = i18nLoader(key);
        deepMerge(current, mod?.default ?? mod);
      } catch {}
    }
    deepMerge(merged, nestedMap);
  }

  if (context) {
    const ctxMod = tryLoad(null, `./${locale}/${namespace}/${context}.json`);
    if (ctxMod) deepMerge(merged, ctxMod?.default ?? ctxMod);
  }

  return merged;
}

/** Expose the eager loader map so TranslationContext can load arbitrary files synchronously. */
export { i18nLoader, i18nKeys };

export default getDictionary;
